import { useState, useCallback, useEffect, useRef } from 'react';
import { DEFAULT_PAGE_SIZE } from '../lib/constants';

interface UsePaginationOptions {
  initialPage?: number;
  initialLimit?: number;
  /**
   * Search/filter values that, when changed, should snap the view back to the
   * first page. Without this, searching while on (say) page 3 leaves you on a
   * now-out-of-range page and the results look empty. Pass the same dependency
   * list you feed the query (debounced search, status filters, sort, etc.).
   */
  resetDeps?: unknown[];
}

export function usePagination(options?: UsePaginationOptions) {
  const [page, setPage] = useState(options?.initialPage ?? 1);
  const [limit, setLimit] = useState(options?.initialLimit ?? DEFAULT_PAGE_SIZE);

  // Reset to page 1 whenever a filter/search dependency changes. Skip the very
  // first run so an explicit initialPage isn't clobbered on mount.
  const resetDeps = options?.resetDeps;
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetDeps ?? []);

  const nextPage = useCallback(() => setPage((p) => p + 1), []);
  const prevPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const goToPage = useCallback((p: number) => setPage(Math.max(1, p)), []);

  const changeLimit = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page when changing limit
  }, []);

  const reset = useCallback(() => {
    setPage(1);
  }, []);

  return {
    page,
    limit,
    nextPage,
    prevPage,
    goToPage,
    changeLimit,
    reset,
  };
}
